import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { memo, useEffect, useState, useCallback, useMemo } from "react";
import { AppsContainer } from "@lib/components/AppComponents";
import { useCustomerQueries } from "@lib/apiQueries";
import { AppContext, SharedContextProps } from "@lib/types/Context";
import { ROUTES } from "@lib/constants";
import { getRoute } from "@lib/helpers/RouteHelper";
import Error404 from "@app/ErrorPages/Error404";
import { CustomerLogo } from "@lib/components/MiniComponents/Others";
import { CustomerModal } from "@lib/components/PageComponents/Customer";
import { SidebarCustomerDetailListItems } from "./SidebarUtils";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiGetCustomerOnlineAgent,
  apiUpdateCustomer,
} from "@lib/api/customer";
import { getCustomerDetailTabs } from "./Tabs";
import { apiGetBillingWalletAddress } from "@lib/api/wallet";
import { apiGetAIWalletAddress } from "@lib/api/walletAI";
import _ from "lodash";

const SharedCustomerDetail: React.FC<SharedContextProps> = ({
  context = AppContext.CUSTOMER,
  customTitle,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { customerId, tab } = useParams();
  // const { customer, permissions } = useWhiteLabel();
  const Tabs = getCustomerDetailTabs();

  const { customerQuery } = useCustomerQueries({
    id: customerId,
    messages,
    queryKey: ["customer", customerId],
  });

  const { data, isLoading, isFetching, refetch } = customerQuery;
  const apiData = data?.data;

  const updateCustomerMutation = useMutation({
    mutationFn: apiUpdateCustomer,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.customer"] as string,
        ),
      );
      refetch();
    },
  });

  useEffect(() => {
    if (!customerId) {
      toast.error("customer ID is undefined");
      navigate(ROUTES.ADMIN.CUSTOMER.LIST);
    }
  }, [customerId]);

  useEffect(() => {
    if (!isLoading && !isFetching && !apiData) {
      navigate(ROUTES.ADMIN.CUSTOMER.LIST);
    }
  }, [isLoading, isFetching, apiData]);

  const [selectedTab, setSelectedTab] = useState<TabKeys>(tab || Tabs[0].key);
  const _keys = Tabs.map((tab) => tab.key);
  type TabKeys = (typeof _keys)[number];

  useEffect(() => {
    handleTabClick(tab || getCustomerDetailTabs()[0].key);
  }, [tab]);

  const handleTabClick = useCallback((key: TabKeys) => {
    setSelectedTab(key);
    navigate(
      getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL_TAB, {
        customerId: customerId,
        tab: key,
      }),
    );
  }, []);

  const customerOnlineAgentQuery = useQuery({
    queryFn: () => apiGetCustomerOnlineAgent(customerId),
    queryKey: ["customer-online-agent", customerId],
    enabled: !!apiData?.id && !apiData?.isWhatsappPlatform,
  });
  const { data: onlineAgentData } = customerOnlineAgentQuery;

  const walletAddressQuery = useQuery({
    queryFn: () =>
      apiGetBillingWalletAddress({
        filter: [`customerId||$eq||${customerId}`],
        sort: "createdAt,DESC",
      }),
    queryKey: ["billing-wallet-address", customerId],
    enabled: !!customerId && !isLoading,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: walletAddressData, isLoading: walletAddressLoading } =
    walletAddressQuery;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const walletAddressApiData = walletAddressData?.data?.data;

  const aiWalletAddressQuery = useQuery({
    queryFn: () =>
      apiGetAIWalletAddress({
        filter: [`customerId||$eq||${customerId}`],
        sort: "createdAt,DESC",
      }),
    queryKey: ["ai-wallet-address", customerId],
    enabled: !!customerId && !isLoading,
  });
  const { data: aiWalletAddressData } = aiWalletAddressQuery;
  const aiWalletAddressApiData = aiWalletAddressData?.data?.data;

  const [openModal, setModalOpen] = useState(false);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const title = useMemo(
    () => customTitle || `${apiData?.name}`,
    [customTitle, apiData?.name, messages],
  );

  if (!isLoading && !apiData) return <Error404 />;

  const ActiveTab = Tabs.find((tab) => tab.key === selectedTab) || Tabs[0];

  return (
    <AppsContainer
      isLoading={isLoading || isFetching}
      title={title}
      breadcrumbProps={{
        urlParams: {
          customerId: `${apiData?.name}`,
        },
      }}
      sidebarContent={
        <AppsContainer.Sidebar>
          <AppsContainer.SidebarUserCard
            // cover={<img src={coverImg} alt="" />}
            avatar={<CustomerLogo customer={apiData} size="large" />}
            title={apiData?.name}
            editAction={() => toggle()}
            listItems={SidebarCustomerDetailListItems({
              customer: apiData,
              isLoading: isLoading || isFetching,
              onlineAgentData: onlineAgentData?.data,
              isWhatsappPlatform: apiData?.isWhatsappPlatform,
              aiCredit: _.round(
                aiWalletAddressApiData?.reduce(
                  (acc, curr) => acc + Number(curr.remainingBalance),
                  0,
                ),
                0,
              ),
              aiFreeCredit: _.round(
                _.sumBy(aiWalletAddressApiData, "freeCredit"),
                0,
              ),
            })}
            loading={isLoading || isFetching}
          />
          {/* <AppsContainer.SidebarUserCard
            header={{
              title: messages["customer.tabs.walletAccounts"] as string,
              extra: (
                <Link
                  to={getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL_TAB, {
                    customerId: customerId,
                    tab: "walletAccounts",
                  })}
                >
                  {messages["common.listAll"] as string}
                </Link>
              ),
            }}
            listItems={SidebarWalletAddressSummaryListItems({
              walletAddressData: walletAddressApiData,
              isLoading: isLoading || walletAddressLoading,
            })}
            loading={isLoading || walletAddressLoading}
          /> */}
          <AppsContainer.SidebarMenu
            data={Tabs.map((tab) => ({
              ...tab,
              isActive: selectedTab === tab.key,
              onClick: () => handleTabClick(tab.key),
              props: {
                customerId: customerId,
                customer: apiData,
                pageIsActive: selectedTab === tab.key,
                withoutContainer: true,
                context: context,
                detailContext: context,
                minusHeight: 50,
              },
            }))}
            loading={isLoading || isFetching}
            onClick={(key: string) => handleTabClick(key as TabKeys)}
            activeKey={selectedTab}
            title={messages["common.menu"] as string}
          />
        </AppsContainer.Sidebar>
      }
      sidebarProviderProps={{ doNotTrack: true }}
      menuProps={{
        collapsed: true,
      }}
      cardStyle={{ marginTop: 0 }}
    >
      {ActiveTab?.Component && (
        <ActiveTab.Component
          key={ActiveTab.key}
          customerId={customerId}
          customer={apiData}
          pageIsActive={selectedTab === ActiveTab.key}
          withoutContainer={true}
          context={context}
          detailContext={context}
          minusHeight={50}
        />
      )}
      <CustomerModal
        mutationQuery={updateCustomerMutation}
        openModal={openModal}
        toggleModal={toggle}
        customer={apiData}
        query={customerQuery}
      />
    </AppsContainer>
  );
};

export default memo(SharedCustomerDetail);
